// Event families the chart draws beyond the per-indicator detectors (DWLF-447). Pure, so each
// is testable without rendering the chart. Every family is detected over the FULL series and
// clipped to the visible window, so a short window (mobile) keeps the detector's warm-up.

const inWindow = (t, from, to) => Number.isFinite(t) && t >= from && t <= to;

// SMC events the chart's overlays don't already mark (DWLF-447): the move into premium /
// discount / equilibrium. Formations (zones and liquidity pools) stay out on purpose: the box or
// line shows a formation, and the chart's SMC markers are drawn only while their zone is.
export const SMC_EXTRA_EVENT_IDS = Object.freeze({
  premiumDiscount: ['smc.range.premium', 'smc.range.discount', 'smc.range.equilibrium'],
});

/** The SMC library's own events (the jobs' detector) of the overlays switched on, full history. */
export const smcExtraEvents = (SMC, { fullCandles, options, overlays }) => {
  const wanted = new Set(Object.entries(SMC_EXTRA_EVENT_IDS)
    .filter(([overlay]) => overlays.has(overlay))
    .flatMap(([, ids]) => ids));
  if (!wanted.size) {return [];}
  return SMC.detectEvents(fullCandles, options).filter((event) => wanted.has(event.id));
};

/** The most recent `maxPerId` events of each id inside [from, to], oldest first per id. */
export const latestPerId = (events, { from, to, maxPerId }) => {
  const byId = new Map();
  for (const event of events) {
    if (!inWindow(event.t, from, to)) {continue;}
    if (!byId.has(event.id)) {byId.set(event.id, []);}
    byId.get(event.id).push(event);
  }
  return [...byId.values()].flatMap((list) => list.sort((a, b) => a.t - b.t).slice(-maxPerId));
};

const STAGE_NAMES = Object.freeze({ 1: 'base', 2: 'advance', 3: 'top', 4: 'decline' });

/**
 * A marker where each Weinstein stage became knowable, from the page's stage read
 * (GET /v2/stages: newest-first history of { to, knowableFrom, baseline }). The baseline row is
 * the series' first stage, not an entry, so it is not marked.
 */
export const weinsteinStageEvents = (stage, { from, to }) => (Array.isArray(stage?.history) ? stage.history : [])
  .filter((h) => !h?.baseline && STAGE_NAMES[h?.to])
  .map((h) => ({ h, t: Date.parse(h.knowableFrom) }))
  .filter(({ t }) => inWindow(t, from, to))
  .map(({ h, t }) => ({
    id: `weinsteinStage.${STAGE_NAMES[h.to]}.entered`,
    name: `Stage ${h.to} (${STAGE_NAMES[h.to]})`,
    description: `Weinstein stage ${h.to}, ${STAGE_NAMES[h.to]}, became knowable (weekly; context, not a signal).`,
    t,
    payload: {},
  }))
  .sort((a, b) => a.t - b.t);

/**
 * Bollinger squeeze spans [{ from, to }] from the library's squeeze / squeeze.released events:
 * a squeeze runs from its event to the next release, or to the last bar while still on.
 */
export const squeezeSpans = (Bollinger, { fullCandles, params }) => {
  const lastT = fullCandles.length ? fullCandles[fullCandles.length - 1].t : null;
  const spans = [];
  let open = null;
  for (const event of Bollinger.detectEvents(fullCandles, params)) {
    if (event.id === 'bollinger.squeeze' && open === null) {open = event.t;}
    if (event.id === 'bollinger.squeeze.released' && open !== null) {
      spans.push({ from: open, to: event.t });
      open = null;
    }
  }
  if (open !== null && Number.isFinite(lastT)) {spans.push({ from: open, to: lastT });}
  return spans;
};

/** Faint shaded areas for the squeeze spans, in the stage band's shape (behind the candles). */
export const buildSqueezeBand = ({ spans, barTimes, minLow, maxHigh, color }) => {
  if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) {return [];}
  const bars = (barTimes || []).filter(Number.isFinite).sort((a, b) => a - b);
  return spans
    .map((span) => ({
      key: `Bollinger squeeze ${new Date(span.from).toISOString()}`,
      type: 'area',
      data: bars.filter((t) => t >= span.from && t <= span.to).map((t) => ({ t, v: maxHigh })),
      style: { color, opacity: 0.08, fullHeight: true },
      showInTooltip: false,
    }))
    .filter((series) => series.data.length >= 2);
};
